<template>
  <div>
    <div class="page-header">
      <h1 class="page-title">Check-in</h1>
      <router-link to="/scan" target="_blank" class="btn-primary">
        Abrir scanner
      </router-link>
    </div>

    <p v-if="!authStore.isCheckin" class="page-help">
      Use o filtro pra selecionar o evento. O scanner abre em outra aba pra usar no celular.
    </p>

    <div v-if="authStore.isCheckin && !authStore.admin?.eventoCheckinId" class="aviso aviso-warn">
      Você ainda não está vinculado a nenhum evento.
    </div>

    <template v-else>
      <div class="filters card">
        <select v-if="!authStore.isCheckin" class="select" v-model="eventoSlug" @change="load">
          <option value="">Todos eventos com check-in</option>
          <option v-for="e in eventosComCheckin" :key="e.id" :value="e.slug">{{ e.nome }}</option>
        </select>
        <input class="input search" v-model="q" @input="debouncedLoad" placeholder="Buscar por nome ou CPF" />
        <select class="select" v-model="statusFilter" @change="load">
          <option value="PENDENTE">Pendentes</option>
          <option value="CONFIRMADO">Confirmados</option>
          <option value="">Todos</option>
        </select>
      </div>

      <div v-if="stats" class="stats card">
        <div class="stat-box">
          <span class="stat-num">{{ stats.confirmados }}</span>
          <span class="stat-lbl">confirmados</span>
        </div>
        <div class="stat-divider"></div>
        <div class="stat-box">
          <span class="stat-num">{{ stats.pendentes }}</span>
          <span class="stat-lbl">faltam</span>
        </div>
        <div class="stat-divider"></div>
        <div class="stat-box">
          <span class="stat-num">{{ stats.percentual }}%</span>
          <span class="stat-lbl">de {{ stats.total }}</span>
        </div>
      </div>

      <SkeletonLoader v-if="loading" variant="row" :count="8" />

      <div v-else-if="inscricoes.length" class="table-card card">
        <div class="table-wrap"><table class="table">
          <thead>
            <tr>
              <th>Nome</th>
              <th class="hide-mobile">CPF</th>
              <th class="hide-mobile">Evento</th>
              <th>Status</th>
              <th>Ação</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="i in inscricoes" :key="i.id" @click="abrirInscricao(i)" class="clickable">
              <td>
                <strong>{{ i.participanteNome }}</strong>
                <FaixaChip :tipo="i.tipoInscricao" />
                <span class="show-mobile sub">{{ i.evento?.nome }}</span>
              </td>
              <td class="hide-mobile mono">{{ i.participanteCpf }}</td>
              <td class="hide-mobile">{{ i.evento?.nome }}</td>
              <td>
                <span v-if="i.checkInStatus === 'CONFIRMADO'" class="chip chip-ok">Confirmado</span>
                <span v-else class="chip chip-pending">Pendente</span>
              </td>
              <td @click.stop>
                <button v-if="i.checkInStatus === 'PENDENTE'" class="btn-sm btn-primary" @click="confirmar(i)">
                  Confirmar
                </button>
                <button v-else class="btn-sm btn-secondary" @click="desfazer(i)">
                  Desfazer
                </button>
              </td>
            </tr>
          </tbody>
        </table></div>
      </div>

      <EmptyState v-else icon="✅" title="Tudo certo" description="Sem inscritos pendentes nesse filtro." />
    </template>

    <ConfirmDialog
      :visible="desfazerTarget !== null"
      title="Desfazer check-in?"
      message="A inscrição vai voltar pra pendente."
      confirmLabel="Desfazer"
      @confirm="executarDesfazer"
      @cancel="desfazerTarget = null"
    />
  </div>
</template>

<script>
import { useAuthStore } from '../../stores/auth'
import { useEventosStore } from '../../stores/eventos'
import { getCheckinLista, getCheckinStats, scanInscricao, uncheckInscricao } from '../../lib/checkin'
import SkeletonLoader from '../../components/ui/SkeletonLoader.vue'
import EmptyState from '../../components/ui/EmptyState.vue'
import ConfirmDialog from '../../components/ui/ConfirmDialog.vue'
import FaixaChip from '../../components/ui/FaixaChip.vue'

export default {
  name: 'CheckinPage',
  components: { SkeletonLoader, EmptyState, ConfirmDialog, FaixaChip },
  inject: ['pusher'],
  data() {
    return {
      inscricoes: [],
      stats: null,
      loading: false,
      eventoSlug: '',
      q: '',
      statusFilter: 'PENDENTE',
      desfazerTarget: null,
      channel: null,
      debounceTimer: null
    }
  },
  computed: {
    authStore() { return useAuthStore() },
    eventosStore() { return useEventosStore() },
    eventosComCheckin() {
      return this.eventosStore.eventos.filter(e => e.temCheckin)
    }
  },
  async mounted() {
    await this.eventosStore.fetchAll()
    if (this.authStore.isCheckin && this.authStore.admin?.eventoCheckinId) {
      const ev = this.eventosStore.eventos.find(e => e.id === this.authStore.admin.eventoCheckinId)
      if (ev) this.eventoSlug = ev.slug
    } else if (this.eventosComCheckin.length === 1) {
      this.eventoSlug = this.eventosComCheckin[0].slug
    }
    await this.load()
    this.channel = this.pusher.subscribe('inscricoes')
    this.channel.bind('checkin-confirmado', () => this.load())
    this.channel.bind('checkin-revertido', () => this.load())
  },
  beforeUnmount() {
    if (this.channel) this.channel.unbind_all()
  },
  methods: {
    debouncedLoad() {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => this.load(), 350)
    },
    async load() {
      this.loading = true
      try {
        const params = {}
        if (this.eventoSlug) params.eventoSlug = this.eventoSlug
        if (this.statusFilter) params.status = this.statusFilter
        if (this.q) params.q = this.q
        const [lst, st] = await Promise.all([
          getCheckinLista(params),
          getCheckinStats(this.eventoSlug ? { eventoSlug: this.eventoSlug } : {})
        ])
        this.inscricoes = lst
        this.stats = st
      } catch (err) {
        this.$toast.open({ message: 'Erro ao carregar lista', type: 'error', position: 'top-right' })
      } finally {
        this.loading = false
      }
    },
    abrirInscricao(i) {
      if (i.checkInStatus === 'CONFIRMADO') this.desfazer(i)
      else this.confirmar(i)
    },
    async confirmar(i) {
      try {
        await scanInscricao(i.id, { dryRun: false })
        this.$toast.open({ message: `${i.participanteNome} confirmado`, type: 'success', position: 'top-right' })
        await this.load()
      } catch (err) {
        this.$toast.open({ message: err.response?.data?.message || 'Erro', type: 'error', position: 'top-right' })
      }
    },
    desfazer(i) { this.desfazerTarget = i },
    async executarDesfazer() {
      const t = this.desfazerTarget
      this.desfazerTarget = null
      try {
        await uncheckInscricao(t.id)
        await this.load()
      } catch (err) {
        this.$toast.open({ message: err.response?.data?.message || 'Erro', type: 'error', position: 'top-right' })
      }
    }
  }
}
</script>

<style scoped>
.page-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: var(--spacing-base); gap: 12px; flex-wrap: wrap; }
.page-help { color: var(--color-text-muted); font-size: var(--text-sm); margin-bottom: var(--spacing-base); }
.aviso { padding: 14px 16px; border-radius: var(--radius-md); margin-bottom: var(--spacing-base); }
.aviso-warn { background: #fef3c7; color: #78350f; }
.filters { display: flex; gap: 8px; padding: 14px; margin-bottom: var(--spacing-base); flex-wrap: wrap; }
.search { flex: 1 1 180px; min-width: 0; }
.select { flex: 1 1 140px; min-width: 0; }
.stats {
  display: flex; align-items: stretch;
  padding: 16px; margin-bottom: var(--spacing-base);
  justify-content: space-around;
}
.stat-box { display: flex; flex-direction: column; align-items: center; gap: 4px; }
.stat-num { font-size: clamp(20px, 4vw, 28px); font-weight: 800; color: var(--color-accent); }
.stat-lbl { font-size: 11px; color: var(--color-text-muted); text-transform: uppercase; letter-spacing: 0.5px; }
.stat-divider { width: 1px; background: var(--color-bg-separator); margin: 0 8px; }
.table-card { padding: 0; overflow-x: auto; }
.table { width: 100%; border-collapse: collapse; }
.table th {
  text-align: left; padding: 10px var(--spacing-base);
  font-size: var(--text-xs); color: var(--color-text-muted);
  text-transform: uppercase; letter-spacing: 0.5px;
  border-bottom: 2px solid var(--color-bg-separator);
}
.table td {
  padding: 12px var(--spacing-base);
  font-size: var(--text-sm);
  border-bottom: 1px solid var(--color-bg-separator);
}
.table tr.clickable { cursor: pointer; }
.table tr:hover td { background: var(--color-bg-hover); }
.mono { font-family: monospace; font-size: var(--text-xs); }
.sub { display: block; font-size: 11px; color: var(--color-text-muted); margin-top: 2px; }
.show-mobile { display: none; }
@media (max-width: 600px) {
  .hide-mobile { display: none; }
  .show-mobile { display: block; }
}
.chip {
  display: inline-block;
  font-size: 11px; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase;
  padding: 3px 8px; border-radius: 4px;
}
.chip-ok { background: #dcfce7; color: #166534; }
.chip-pending { background: #fef3c7; color: #92400e; }
.btn-sm { padding: 6px 12px; font-size: var(--text-xs); }
</style>
